import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, RefreshCw, Search,
  CheckCircle2, FileText, Building2, Download,
} from 'lucide-react';

import { bankReconciliationApi } from '@/api/bankReconciliation.api';
import { usePermission }         from '@/hooks/usePermission';
import { Permissions }           from '@/constants/permissions';
import { AppRoutes }             from '@/constants/routes';
import { Spinner }               from '@/components/ui/Spinner';
import { DateRangeFilter } from '@/components/lists/ListFilters';
import { ListPager } from '@/components/lists/ListPager';
import { cn }                    from '@/utils/cn';

import type {
  BankReconciliationListDto,
  BankReconciliationFilterDto,
  ReconciliationStatus,
} from '@/types/bankReconciliation.types';
import {
  RECONCILIATION_STATUS_LABELS,
  RECONCILIATION_STATUS_COLORS,
} from '@/types/bankReconciliation.types';

// ── Status icon ───────────────────────────────────────────────────────────────
const StatusIcon = ({ status }: { status: ReconciliationStatus }) => {
  const cls = 'shrink-0';
  switch (status) {
    case 'Completed': return <CheckCircle2 size={14} className={`${cls} text-green-500`} />;
    case 'Submitted': return <FileText     size={14} className={`${cls} text-blue-500`} />;
    default:          return <FileText     size={14} className={`${cls} text-amber-500`} />;
  }
};

// ── Difference badge ──────────────────────────────────────────────────────────
const DiffBadge = ({ diff }: { diff: number }) => {
  const balanced = Math.abs(diff) < 0.005;
  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold font-mono',
      balanced
        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
    )}>
      {balanced ? '✓ Balanced' : diff.toLocaleString('en-US', { minimumFractionDigits: 2 })}
    </span>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
export const BankReconciliationListPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate         = hasPermission(Permissions.BNK_RECONCILE_CREATE);
  const canExport         = hasPermission(Permissions.BNK_RECONCILE_EXPORT);

  const [items,   setItems]   = useState<BankReconciliationListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const [filter, setFilter] = useState<BankReconciliationFilterDto>({
    pageNumber: 1,
    pageSize,
  });

  const [search, setSearch] = useState('');

  const load = useCallback(async (f: BankReconciliationFilterDto) => {
    setLoading(true);
    try {
      const res  = await bankReconciliationApi.getList(f);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } catch (err) {
      console.error('Failed to load bank reconciliations', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const handleSearch = () => {
    setFilter(f => ({ ...f, reconciliationNumber: search || undefined, pageNumber: 1 }));
    setPage(1);
  };

  const handleStatusFilter = (status: string) => {
    setFilter(f => ({
      ...f,
      status: f.status === status ? undefined : status,
      pageNumber: 1,
    }));
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilter(f => ({ ...f, pageNumber: newPage }));
  };

  const totalPages = Math.ceil(total / pageSize);

  const handleExportCsv = () => {
    if (items.length === 0) return;
    const headers = [
      'Reconciliation #', 'Bank Account Code', 'Bank Account Name', 'Currency',
      'Statement Date', 'Statement Closing', 'Book Closing', 'Difference',
      'Status', 'Completed Date', 'Created Date',
    ];
    const rows = items.map(i => [
      i.reconciliationNumber,
      i.bankAccountCode,
      i.bankAccountName,
      i.currencyCode,
      i.statementDate ? new Date(i.statementDate).toLocaleDateString('en-GB') : '',
      i.statementClosingBalance.toFixed(2),
      i.bookClosingBalance.toFixed(2),
      i.difference.toFixed(2),
      i.status,
      i.completedDate ? new Date(i.completedDate).toLocaleDateString('en-GB') : '',
      i.createdDate ? new Date(i.createdDate).toLocaleDateString('en-GB') : '',
    ]);
    const csvContent = [headers, ...rows]
      .map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = `bank-reconciliations-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const fmtAmt = (n: number) =>
    n.toLocaleString('en-US', { minimumFractionDigits: 2 });

  const statusCounts = {
    Draft:     items.filter(i => i.status === 'Draft').length,
    Submitted: items.filter(i => i.status === 'Submitted').length,
    Completed: items.filter(i => i.status === 'Completed').length,
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Building2 size={20} className="text-teal-600" />
              Bank Reconciliation
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total.toLocaleString()} total reconciliations
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(filter)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            {canExport && items.length > 0 && (
              <button
                onClick={handleExportCsv}
                className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium rounded-lg transition-colors"
                title="Export to CSV"
              >
                <Download size={15} />
                Export CSV
              </button>
            )}
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.BANK_RECONCILE_NEW)}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus size={15} />
                New Reconciliation
              </button>
            )}
          </div>
        </div>

        {/* ── Status summary ──────────────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-2 mt-4 max-w-sm">
          {([
            { label: 'Draft',     status: 'Draft',     count: statusCounts.Draft,     icon: <FileText     size={14} />, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20',  ring: 'ring-amber-200' },
            { label: 'Submitted', status: 'Submitted', count: statusCounts.Submitted, icon: <FileText     size={14} />, color: 'text-blue-600 dark:text-blue-400',  bg: 'bg-blue-50 dark:bg-blue-900/20',    ring: 'ring-blue-200'  },
            { label: 'Completed', status: 'Completed', count: statusCounts.Completed, icon: <CheckCircle2 size={14} />, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20',  ring: 'ring-green-200' },
          ] as const).map(stat => {
            const isActive = filter.status === stat.status;
            return (
              <button
                key={stat.label}
                onClick={() => handleStatusFilter(stat.status)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg ring-1 transition-all text-left',
                  'hover:ring-2 hover:shadow-sm active:scale-95',
                  stat.bg, stat.ring,
                  isActive && 'ring-2 shadow-md scale-[0.98]',
                )}
              >
                <span className={stat.color}>{stat.icon}</span>
                <div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-none">{stat.label}</p>
                  <p className={cn('text-sm font-bold leading-tight', stat.color)}>{stat.count}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Filters ─────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Search reconciliation #…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Status pills */}
          <div className="flex items-center gap-1.5">
            {(['Draft', 'Completed'] as const).map(s => (
              <button
                key={s}
                onClick={() => handleStatusFilter(s)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
                  filter.status === s
                    ? (RECONCILIATION_STATUS_COLORS[s] ?? '') + ' ring-2 ring-offset-1 ring-current'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                )}
              >
                {RECONCILIATION_STATUS_LABELS[s]}
              </button>
            ))}
          </div>

          {/* Financial Year quick-select */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">Year</label>
            <select
              value=""
              onChange={e => {
                const yr = e.target.value;
                if (!yr) {
                  setFilter(f => ({ ...f, statementDateFrom: undefined, statementDateTo: undefined, pageNumber: 1 }));
                } else {
                  setFilter(f => ({
                    ...f,
                    statementDateFrom: `${yr}-01-01`,
                    statementDateTo:   `${yr}-12-31`,
                    pageNumber: 1,
                  }));
                }
                setPage(1);
              }}
              className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1.5
                         bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-teal-500 outline-none"
            >
              <option value="">All years</option>
              {[new Date().getFullYear() + 1, new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map(yr => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </div>

          <DateRangeFilter
            label="Statement date"
            from={filter.statementDateFrom}
            to={filter.statementDateTo}
            onChange={({ from, to }) => {
              setFilter(f => ({ ...f, statementDateFrom: from, statementDateTo: to, pageNumber: 1 }));
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Building2 size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No bank reconciliations found</p>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.BANK_RECONCILE_NEW)}
                className="mt-3 text-teal-600 text-sm hover:underline"
              >
                Create your first reconciliation
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Reference #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Bank Account</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Statement Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Ccy</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Statement Balance</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Book Balance</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Difference</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Completed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(rec => (
                <tr
                  key={rec.id}
                  onClick={() => navigate(AppRoutes.BANK_RECONCILE_DETAIL.replace(':id', rec.id))}
                  className="hover:bg-teal-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors group"
                >
                  <td className="px-4 py-3 font-mono font-semibold text-teal-700 dark:text-teal-400 group-hover:text-teal-800">
                    {rec.reconciliationNumber}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 dark:text-white">{rec.bankAccountCode}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[180px]">{rec.bankAccountName}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                    {fmtDate(rec.statementDate)}
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 font-mono text-xs">
                    {rec.currencyCode}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">
                    {fmtAmt(rec.statementClosingBalance)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">
                    {fmtAmt(rec.bookClosingBalance)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <DiffBadge diff={rec.difference} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1.5">
                      <StatusIcon status={rec.status} />
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        RECONCILIATION_STATUS_COLORS[rec.status]
                      )}>
                        {RECONCILIATION_STATUS_LABELS[rec.status]}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                    {rec.completedDate ? fmtDate(rec.completedDate) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ───────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <ListPager
          page={page}
          totalPages={totalPages}
          totalCount={total}
          onChange={handlePageChange}
          noun="reconciliations"
        />
      )}
    </div>
  );
};
